// = Libs =
import useSWR from "swr";
import PropTypes from "@/common/types/prop.types";
import { QuestionModel, SectionModel } from "@/services/question.service";
import sendRequest from "@/helpers/sendRequest";
import { formatReportStatus } from "@/helpers/formatStatus";
import formatDate from "@/helpers/formatDate";

// = Helpers =

interface BEReportModel {
  reportId: number;
  status: string; //TODO: модель статусов
  executor?: {
    organizationId: number;
    attributes: {
      title: {
        value: string;
      };
    };
  };
  executor_user?: {
    userId: number;
    fullName: string;
    username: string;
  };
  comments?: {
    commentId: number;
    content: string;
  }[];
  createdAt: string;
  updatedAt: string;
}

interface ReportCardModel {
  id: number;
  status?: string;
  statusColor?: PropTypes.Color;
  title?: string;
  createDt?: string;
  updateDt?: string;
  comments?: string[];
  link?: string;
  buttons?: boolean;
  executorId?: number;
}

const mapToReportCard = (r: BEReportModel): ReportCardModel => {
  return {
    id: r.reportId,
    status: formatReportStatus(r.status).text,
    statusColor: formatReportStatus(r.status).color,
    title: r.executor?.attributes.title.value || r.executor_user?.fullName,
    comments: r.comments?.map((c) => c.content) || [],
    link: `/reports/${r.reportId}/view/`,
    buttons: r.status === 'review',
    updateDt: formatDate(r.updatedAt),
    createDt: formatDate(r.createdAt),
    executorId: r.executor?.organizationId || r.executor_user?.userId,
  };
};

const useReports = (pollId: number | string) => {
  const { data, error } = useSWR(`monitoring-api/polls/${pollId}/reports`);

  if (!data) return error;

  return {
    total: data.total,
    count: data.items.length,
    elementList: data.items.map(mapToReportCard),
  };
};

const useReportQuestions = (reportId: number): QuestionModel[] => {
  const { data, error } = useSWR(
    `monitoring-api/reports/${reportId}/questions`
  );

  if (!data) return error;

  return data?.items;
};

const getReportQuestions = async (reportId: number | string) => {
  const { error, data } = await sendRequest<{ items: QuestionModel[] }>({
    url: `monitoring-api/reports/${reportId}/questions/`,
    method: 'get',
  })
    .then((res) => res)
    .catch((err) => err);

  return { error, data };
};

const useReportSections = (reportId: number | string): SectionModel[] => {
  const { data, error } = useSWR(`monitoring-api/reports/${reportId}/sections`);

  if (!data) return error;

  return data?.items;
};

const createReport = async (
  pollId: number,
  orgId: number,
  isPublic: boolean = false
) => {
  const { error, data: BEReportModel } = await sendRequest<{
    reportId: number;
  }>({
    url: `monitoring-api/polls/${pollId}/report/` + (isPublic ? 'public/' : ''),
    method: 'post',
    body: {
      'executor': orgId,
    },
  })
    .then((res) => res)
    .catch((err) => err);

  return { error, id: BEReportModel?.reportId };
};

const saveAnswer = async (
  reportId: number,
  questionId: number,
  value: string
) => {
  const { error, data: QuestionModel } = await sendRequest({
    url: `monitoring-api/reports/${reportId}/questions/${questionId}/`,
    method: 'patch',
    body: {
      'answer': {
        'value': value,
      },
    },
  })
    .then((res) => res)
    .catch((err) => err);

  return { error, id: QuestionModel?.questionId };
};

const commitReport = async (
  reportId: number,
  fileId: number,
  confirmRequired: boolean = true
) => {
  const { error } = await sendRequest({
    url: `monitoring-api/reports/${reportId}/${
      !confirmRequired ? 'force-' : ''
    }commit/`,
    method: 'patch',
    body: confirmRequired
      ? {
          'signedDocument': fileId,
        }
      : undefined,
  })
    .then((res) => res)
    .catch((err) => err);

  return { error };
};
const acceptReport = async (reportId: number) => {
  const { error } = await sendRequest({
    url: `monitoring-api/reports/${reportId}/commit/accept/`,
    method: 'patch',
  })
    .then((res) => res)
    .catch((err) => err);

  return { error };
};
const rejectReport = async (reportId: number) => {
  const { error } = await sendRequest({
    url: `monitoring-api/reports/${reportId}/commit/reject/`,
    method: 'patch',
  })
    .then((res) => res)
    .catch((err) => err);

  return { error };
};

export {
  useReports,
  getReportQuestions,
  useReportQuestions,
  useReportSections,
  saveAnswer,
  createReport,
  acceptReport,
  rejectReport,
  commitReport,
};
export type { ReportCardModel };
